/* eslint-disable react/prop-types */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";

import { toast } from "react-toastify";

import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import Loading from "../../components/Loading";

import queryString from "../../functions/queryString";

import {
  closeTicket,
  getSingleTicket,
  getTickets,
  sendTicketMessage,
} from "../../api/routes";
import imageCompression from "browser-image-compression";

const STATUS = {
  1: {
    label: "باز",
    color: "#22c55e",
    bg: "rgba(34,197,94,.10)",
  },
  2: {
    label: "در حال بررسی",
    color: "#f59e0b",
    bg: "rgba(245,158,11,.10)",
  },
  3: {
    label: "پاسخ داده شده",
    color: "#3b82f6",
    bg: "rgba(59,130,246,.10)",
  },
  4: {
    label: "بسته",
    color: "#64748b",
    bg: "rgba(100,116,139,.10)",
  },
};

const DEPARTMENT = {
  1: "عمومی",
  2: "مالی",
  3: "فنی",
  4: "کسب‌وکار",
};

const formatDate = (value) => {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat("fa-IR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const getTicketList = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response)) return response;

  return [];
};

const getPaginationMeta = (response) => {
  const meta =
    response?.meta ??
    response?.data?.meta ??
    {};

  return {
    nextCursor: meta.next_cursor ?? null,
    prevCursor: meta.prev_cursor ?? null,
    perPage: meta.per_page ?? 20,
  };
};

const getUserName = (ticket) => {
  const name = [
    ticket?.user?.first_name,
    ticket?.user?.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  return name || `کاربر #${ticket?.user_id ?? "-"}`;
};

const StatusBadge = ({ status }) => {
  const item = STATUS[status] || STATUS[4];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.7,
        height: 26,
        px: 1.2,
        borderRadius: "8px",
        bgcolor: item.bg,
        color: item.color,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: item.color,
        }}
      />

      {item.label}
    </Box>
  );
};
const AttachmentViewer = ({ url, support }) => {
  const [imageState, setImageState] = useState("loading");

  useEffect(() => {
    if (!url) return;

    let active = true;

    const image = new Image();

    image.onload = () => {
      if (active) {
        setImageState("image");
      }
    };

    image.onerror = () => {
      if (active) {
        setImageState("file");
      }
    };

    image.src = url;

    return () => {
      active = false;
    };
  }, [url]);

  if (!url) {
    return null;
  }

  if (imageState === "loading") {
    return (
      <Box
        sx={{
          mt: 1.2,
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress
          size={16}
          sx={{
            color: support
              ? "rgba(255,255,255,.65)"
              : "primary.main",
          }}
        />
      </Box>
    );
  }

  if (imageState === "image") {
    return (
      <Box
        onClick={() => window.open(url, "_blank")}
        sx={{
          mt: 1.3,
          cursor: "pointer",
          overflow: "hidden",
          borderRadius: 2,
          bgcolor: support
            ? "rgba(255,255,255,.06)"
            : "#f8fafc",
          border: support
            ? "1px solid rgba(255,255,255,.1)"
            : "1px solid #e8edf3",
          maxWidth: 360,
          transition: "all .15s ease",

          "&:hover": {
            opacity: 0.92,
          },
        }}
      >
        <Box
          component="img"
          src={url}
          alt="پیوست تیکت"
          loading="lazy"
          sx={{
            display: "block",
            width: "100%",
            maxHeight: 300,
            objectFit: "contain",
            bgcolor: support
              ? "rgba(0,0,0,.15)"
              : "#f8fafc",
          }}
        />

        <Box
          sx={{
            px: 1.2,
            py: 0.8,
            display: "flex",
            alignItems: "center",
            gap: 0.7,
            color: support
              ? "rgba(255,255,255,.65)"
              : "#64748b",
          }}
        >
          <OpenInNewRoundedIcon
            sx={{ fontSize: 13 }}
          />

          <Typography fontSize={9.5}>
            مشاهده تصویر در اندازه کامل
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Button
      size="small"
      startIcon={
        <OpenInNewRoundedIcon
          sx={{
            fontSize: "14px !important",
          }}
        />
      }
      onClick={() => window.open(url, "_blank")}
      sx={{
        mt: 1,
        px: 0,
        minWidth: 0,
        color: support
          ? "rgba(255,255,255,.75)"
          : "primary.main",
        fontSize: 10,
      }}
    >
      مشاهده پیوست
    </Button>
  );
};
export default function TicketManagement() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);
  const [currentCursor, setCurrentCursor] = useState(null);
  const [page, setPage] = useState(1);

  const [status, setStatus] = useState("");
  const [department, setDepartment] = useState("");

  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);

  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const messagesEndRef = useRef(null);

  const loadTickets = (
    targetCursor = null,
    targetPage = 1
  ) => {
    setLoading(true);

    const query = queryString({
      per_page: 20,
      cursor: targetCursor || null,
      status: status || null,
      department: department || null,
    });

    getTickets(query, (isOk, data) => {
      setLoading(false);

      if (!isOk) {
        toast.error(
          data?.message ||
          "دریافت تیکت‌ها انجام نشد."
        );
        return;
      }

      const list = getTicketList(data);
      const pagination =
        getPaginationMeta(data);

      setTickets(list);

      setNextCursor(pagination.nextCursor);
      setPrevCursor(pagination.prevCursor);

      setCurrentCursor(targetCursor);
      setPage(targetPage);
    });
  };

  useEffect(() => {
    setCurrentCursor(null);
    setPage(1);

    loadTickets(null, 1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, department]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
      });
    }, 80);
  };

  const openTicket = (ticket) => {
    setSelected(ticket);
    setDetailLoading(true);
    setMessage("");
    setAttachment(null);

    getSingleTicket(
      ticket.id,
      (isOk, data) => {
        setDetailLoading(false);

        if (!isOk) {
          setSelected(null);

          toast.error(
            data?.message ||
            "دریافت تیکت انجام نشد."
          );

          return;
        }

        setSelected(data);

        setTickets((prev) =>
          prev.map((item) =>
            item.id === data.id
              ? { ...item, ...data }
              : item
          )
        );

        scrollToBottom();
      }
    );
  };

  const allMessages = useMemo(() => {
    if (!selected) return [];

    return [
      {
        id: `initial-${selected.id}`,
        role: "USER",
        message: selected.description,
        attachment: selected.attachment,
        created_at: selected.created_at,
      },
      ...(selected.messages || []),
    ];
  }, [selected]);

  const handleSend = async (event) => {
    event.preventDefault();

    if (
      !selected ||
      !message.trim() ||
      selected.status === 4
    ) {
      return;
    }

    if (
      attachment &&
      attachment.size > 5 * 1024 * 1024
    ) {
      toast.error(
        "حجم فایل نباید بیشتر از ۵ مگابایت باشد."
      );

      return;
    }

    setSending(true);
    const form = new FormData();

    form.append("message", message.trim());

    if (attachment) {
      const compressedImage = await imageCompression(attachment, {
        maxSizeMB: 0.5,
      });

      form.append(
        "attachment",
        compressedImage,
        attachment.name
      );
    }


    sendTicketMessage(
      selected.id,
      form,
      (isOk, data) => {
        setSending(false);

        if (!isOk) {
          toast.error(
            data?.message ||
            "ارسال پاسخ انجام نشد."
          );

          return;
        }

        setSelected((prev) => ({
          ...prev,
          status: 3,
          messages: [
            ...(prev.messages || []),
            data,
          ],
          messages_count:
            (prev.messages_count || 0) + 1,
        }));

        setTickets((prev) =>
          prev.map((item) =>
            item.id === selected.id
              ? {
                ...item,
                status: 3,
                updated_at:
                  data?.created_at ||
                  new Date().toISOString(),
                messages_count:
                  (item.messages_count || 0) +
                  1,
              }
              : item
          )
        );

        setMessage("");
        setAttachment(null);

        scrollToBottom();

        toast.success("پاسخ ارسال شد.");
      }
    );
  };

  const handleCloseTicket = () => {
    if (!selected) return;

    setClosing(true);

    closeTicket(
      selected.id,
      (isOk, data) => {
        setClosing(false);

        if (!isOk) {
          toast.error(
            data?.message ||
            "بستن تیکت انجام نشد."
          );

          return;
        }

        setSelected((prev) => ({
          ...prev,
          ...data,
          status: 4,
        }));

        setTickets((prev) =>
          prev.map((item) =>
            item.id === selected.id
              ? {
                ...item,
                ...data,
                status: 4,
              }
              : item
          )
        );

        setConfirmClose(false);

        toast.success("تیکت بسته شد.");
      }
    );
  };

  return (
    <DashboardLayout>
      <Loading loading={loading} />

      <DashboardNavbar />

      <Box
        dir="rtl"
        sx={{
          py: 3,
          maxWidth: 1500,
          mx: "auto",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 2.5,
            display: "flex",
            flexDirection: {
              xs: "column",
              md: "row",
            },
            alignItems: {
              xs: "stretch",
              md: "center",
            },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                color: "#1e293b",
                letterSpacing: "-0.3px",
              }}
            >
              مدیریت تیکت‌ها
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: "#94a3b8",
              }}
            >
              گفتگو و رسیدگی به درخواست‌های
              پشتیبانی
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            alignItems={{
              xs: "stretch",
              sm: "center",
            }}
          >
            <Select
              size="small"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value)
              }
              displayEmpty
              sx={{
                minWidth: 145,
                height: 40,
                borderRadius: 2,
                bgcolor: "#fff",
                fontSize: 12,
              }}
              startAdornment={
                <FilterAltOutlinedIcon
                  sx={{
                    ml: 1,
                    color: "#94a3b8",
                    fontSize: 18,
                  }}
                />
              }
            >
              <MenuItem value="">
                همه وضعیت‌ها
              </MenuItem>

              <MenuItem value="1">
                باز
              </MenuItem>

              <MenuItem value="2">
                در حال بررسی
              </MenuItem>

              <MenuItem value="3">
                پاسخ داده شده
              </MenuItem>

              <MenuItem value="4">
                بسته
              </MenuItem>
            </Select>

            <Select
              size="small"
              value={department}
              onChange={(e) =>
                setDepartment(e.target.value)
              }
              displayEmpty
              sx={{
                minWidth: 140,
                height: 40,
                borderRadius: 2,
                bgcolor: "#fff",
                fontSize: 12,
              }}
            >
              <MenuItem value="">
                همه بخش‌ها
              </MenuItem>

              <MenuItem value="1">
                عمومی
              </MenuItem>

              <MenuItem value="2">
                مالی
              </MenuItem>

              <MenuItem value="3">
                فنی
              </MenuItem>

              <MenuItem value="4">
                کسب‌وکار
              </MenuItem>
            </Select>

            <Tooltip title="بروزرسانی">
              <IconButton
                onClick={() =>
                  loadTickets(
                    currentCursor,
                    page
                  )
                }
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "#fff",
                  border:
                    "1px solid #e7ebf0",
                  borderRadius: 2,

                  "&:hover": {
                    bgcolor: "#f8fafc",
                  },
                }}
              >
                <RefreshRoundedIcon
                  sx={{
                    fontSize: 19,
                    color: "#64748b",
                  }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Ticket List */}
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: 3,
            border:
              "1px solid #e8edf3",
            bgcolor: "#fff",
          }}
        >
          {tickets.length > 0 && (
            <Box
              sx={{
                display: {
                  xs: "none",
                  md: "grid",
                },
                gridTemplateColumns:
                  "minmax(300px, 1fr) 180px 140px 130px 160px",
                alignItems: "center",
                px: 2.5,
                py: 1.4,
                bgcolor: "#f8fafc",
                borderBottom:
                  "1px solid #edf1f5",
              }}
            >
              {[
                "موضوع",
                "کاربر",
                "بخش",
                "وضعیت",
                "آخرین فعالیت",
              ].map((item) => (
                <Typography
                  key={item}
                  variant="caption"
                  sx={{
                    color: "#94a3b8",
                    fontWeight: 700,
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          )}

          {!loading &&
            tickets.length === 0 && (
              <Box
                sx={{
                  py: 9,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    mx: "auto",
                    mb: 2,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "center",
                    bgcolor: "#f8fafc",
                    color: "#cbd5e1",
                  }}
                >
                  <ChatBubbleOutlineRoundedIcon
                    sx={{ fontSize: 27 }}
                  />
                </Box>

                <Typography
                  fontSize={14}
                  fontWeight={600}
                  color="#64748b"
                >
                  تیکتی پیدا نشد
                </Typography>

                <Typography
                  fontSize={11}
                  color="#a0aec0"
                  mt={0.5}
                >
                  فیلترهای انتخاب شده را
                  تغییر دهید
                </Typography>
              </Box>
            )}

          {tickets.map(
            (ticket, index) => (
              <Box
                key={ticket.id}
                onClick={() =>
                  openTicket(ticket)
                }
                sx={{
                  cursor: "pointer",
                  px: 2.5,
                  py: 2,

                  borderBottom:
                    index !==
                      tickets.length - 1
                      ? "1px solid #f0f2f5"
                      : "none",

                  display: {
                    xs: "block",
                    md: "grid",
                  },

                  gridTemplateColumns:
                    "minmax(300px, 1fr) 180px 140px 130px 160px",

                  alignItems: "center",

                  gap: {
                    xs: 1.5,
                    md: 0,
                  },

                  transition:
                    "background .15s ease",

                  "&:hover": {
                    bgcolor: "#fafbfd",
                  },
                }}
              >
                {/* Subject */}
                <Box sx={{ minWidth: 0 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.2}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        flexShrink: 0,
                        borderRadius:
                          "10px",

                        bgcolor:
                          ticket.status ===
                            1
                            ? "rgba(34,197,94,.08)"
                            : "#f8fafc",

                        color:
                          ticket.status ===
                            1
                            ? "#22c55e"
                            : "#94a3b8",

                        display: "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                      }}
                    >
                      <ChatBubbleOutlineRoundedIcon
                        sx={{
                          fontSize: 17,
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        fontSize={13}
                        fontWeight={
                          ticket.status === 1
                            ? 700
                            : 600
                        }
                        color="#334155"
                        noWrap
                      >
                        {ticket.title}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* User */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <Avatar
                    sx={{
                      width: 26,
                      height: 26,
                      bgcolor: "#f1f5f9",
                      color: "#64748b",
                    }}
                  >
                    <PersonOutlineRoundedIcon
                      sx={{
                        fontSize: 15,
                      }}
                    />
                  </Avatar>

                  <Typography
                    fontSize={11.5}
                    color="#64748b"
                    noWrap
                  >
                    {getUserName(ticket)}
                  </Typography>
                </Stack>

                {/* Department */}
                <Typography
                  fontSize={11.5}
                  color="#64748b"
                >
                  {DEPARTMENT[
                    ticket.department
                  ] || "-"}
                </Typography>

                {/* Status */}
                <Box>
                  <StatusBadge
                    status={ticket.status}
                  />
                </Box>

                {/* Date */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.7}
                >
                  <AccessTimeRoundedIcon
                    sx={{
                      fontSize: 14,
                      color: "#cbd5e1",
                    }}
                  />

                  <Typography
                    fontSize={10.5}
                    color="#94a3b8"
                  >
                    {formatDate(
                      ticket.updated_at ||
                      ticket.created_at
                    )}
                  </Typography>
                </Stack>
              </Box>
            )
          )}
        </Paper>

        {/* Pagination */}
        {tickets.length > 0 && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              alignItems: "center",
              justifyContent:
                "space-between",
              gap: 1.5,
              px: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                color: "#94a3b8",
              }}
            >
              صفحه{" "}
              {page.toLocaleString(
                "fa-IR"
              )}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Button
                variant="outlined"
                size="small"
                disabled={
                  !prevCursor || loading
                }
                onClick={() =>
                  loadTickets(
                    prevCursor,
                    Math.max(
                      1,
                      page - 1
                    )
                  )
                }
                startIcon={
                  <NavigateNextRoundedIcon
                    sx={{
                      fontSize:
                        "17px !important",
                    }}
                  />
                }
                sx={{
                  height: 36,
                  minWidth: 90,
                  borderRadius: 2,
                  borderColor:
                    "#e2e8f0",
                  color: "#64748b",
                  fontSize: 11,
                  boxShadow: "none",

                  "&:hover": {
                    borderColor:
                      "#cbd5e1",
                    bgcolor: "#f8fafc",
                  },

                  "&.Mui-disabled": {
                    borderColor:
                      "#f1f5f9",
                    color: "#cbd5e1",
                  },
                }}
              >
                قبلی
              </Button>

              <Box
                sx={{
                  minWidth: 36,
                  height: 36,
                  px: 1.2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "center",
                  borderRadius: 2,
                  bgcolor:
                    "primary.main",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {page.toLocaleString(
                  "fa-IR"
                )}
              </Box>

              <Button
                variant="outlined"
                size="small"
                disabled={
                  !nextCursor || loading
                }
                onClick={() =>
                  loadTickets(
                    nextCursor,
                    page + 1
                  )
                }
                endIcon={
                  <NavigateBeforeRoundedIcon
                    sx={{
                      fontSize:
                        "17px !important",
                    }}
                  />
                }
                sx={{
                  height: 36,
                  minWidth: 90,
                  borderRadius: 2,
                  borderColor:
                    "#e2e8f0",
                  color: "#64748b",
                  fontSize: 11,
                  boxShadow: "none",

                  "&:hover": {
                    borderColor:
                      "#cbd5e1",
                    bgcolor: "#f8fafc",
                  },

                  "&.Mui-disabled": {
                    borderColor:
                      "#f1f5f9",
                    color: "#cbd5e1",
                  },
                }}
              >
                بعدی
              </Button>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Ticket details */}
      <Dialog
        open={!!selected}
        onClose={() =>
          !sending && setSelected(null)
        }
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: {
              xs: "92vh",
              md: "80vh",
            },

            maxHeight: 760,

            borderRadius: {
              xs: "18px 18px 0 0",
              md: 3,
            },

            m: {
              xs: 0,
              md: 2,
            },

            position: {
              xs: "absolute",
              md: "relative",
            },

            bottom: {
              xs: 0,
              md: "auto",
            },

            overflow: "hidden",

            boxShadow:
              "0 24px 70px rgba(15,23,42,.18)",
          },
        }}
      >
        {selected && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
            dir="rtl"
          >
            {/* Header */}
            <Box
              sx={{
                px: 2.5,
                py: 2,
                borderBottom:
                  "1px solid #edf1f5",
                bgcolor: "#fff",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "#f2f5ff",
                      color:
                        "primary.main",
                    }}
                  >
                    <SupportAgentRoundedIcon
                      fontSize="small"
                    />
                  </Avatar>

                  <Box
                    sx={{
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      fontSize={14}
                      fontWeight={700}
                      color="#334155"
                      noWrap
                    >
                      {selected.title}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      mt={0.6}
                      flexWrap="wrap"
                    >
                      <Typography
                        fontSize={10.5}
                        color="#94a3b8"
                      >
                        {getUserName(
                          selected
                        )}
                      </Typography>

                      <Box
                        sx={{
                          width: 3,
                          height: 3,
                          bgcolor:
                            "#cbd5e1",
                          borderRadius:
                            "50%",
                        }}
                      />

                      <Typography
                        fontSize={10.5}
                        color="#94a3b8"
                      >
                        {
                          DEPARTMENT[
                          selected
                            .department
                          ]
                        }
                      </Typography>

                      <StatusBadge
                        status={
                          selected.status
                        }
                      />
                    </Stack>
                  </Box>
                </Stack>

                <IconButton
                  onClick={() =>
                    setSelected(null)
                  }
                  size="small"
                  sx={{
                    bgcolor: "#f8fafc",
                    color: "#64748b",

                    "&:hover": {
                      bgcolor: "#f1f5f9",
                    },
                  }}
                >
                  <CloseRoundedIcon
                    fontSize="small"
                  />
                </IconButton>
              </Stack>
            </Box>

            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                bgcolor: "#f8fafc",

                px: {
                  xs: 2,
                  sm: 3,
                },

                py: 3,
              }}
            >
              {detailLoading ? (
                <Box
                  height="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <CircularProgress
                    size={26}
                  />
                </Box>
              ) : (
                <Stack spacing={2.2}>
                  {allMessages.map(
                    (item) => {
                      const support =
                        item.role ===
                        "SUPPORT";

                      return (
                        <Box
                          key={item.id}
                          display="flex"
                          justifyContent={
                            support
                              ? "flex-start"
                              : "flex-end"
                          }
                        >
                          <Box
                            sx={{
                              maxWidth: {
                                xs: "88%",
                                sm: "72%",
                              },
                            }}
                          >
                            <Typography
                              fontSize={10}
                              color="#94a3b8"
                              mb={0.7}
                              px={0.5}
                            >
                              {support
                                ? "پشتیبانی"
                                : getUserName(
                                  selected
                                )}

                              {"  ·  "}

                              {formatDate(
                                item.created_at
                              )}
                            </Typography>

                            <Box
                              sx={{
                                bgcolor:
                                  support
                                    ? "#334155"
                                    : "#fff",

                                color:
                                  support
                                    ? "#fff"
                                    : "#334155",

                                px: 1.8,
                                py: 1.4,

                                borderRadius:
                                  support
                                    ? "16px 16px 4px 16px"
                                    : "16px 16px 16px 4px",

                                border:
                                  support
                                    ? "none"
                                    : "1px solid #e8edf3",

                                boxShadow:
                                  support
                                    ? "0 5px 14px rgba(51,65,85,.12)"
                                    : "0 3px 10px rgba(15,23,42,.03)",
                              }}
                            >
                              <Typography
                                fontSize={12}
                                sx={{
                                  whiteSpace:
                                    "pre-wrap",
                                  wordBreak:
                                    "break-word",
                                  lineHeight: 2,
                                }}
                              >
                                {
                                  item.message
                                }
                              </Typography>

                              {item.attachment && (
                                <AttachmentViewer
                                  url={item.attachment}
                                  support={support}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                  )}

                  <div
                    ref={
                      messagesEndRef
                    }
                  />
                </Stack>
              )}
            </Box>

            {/* Composer */}
            {selected.status === 4 ? (
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  borderTop:
                    "1px solid #edf1f5",
                  bgcolor: "#fff",
                }}
              >
                <Box
                  sx={{
                    py: 1.2,
                    px: 2,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    color: "#94a3b8",

                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "center",
                    gap: 1,
                  }}
                >
                  <CheckCircleOutlineRoundedIcon
                    sx={{
                      fontSize: 17,
                    }}
                  />

                  <Typography
                    fontSize={11}
                  >
                    این تیکت بسته شده است
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                component="form"
                onSubmit={handleSend}
                sx={{
                  borderTop:
                    "1px solid #edf1f5",
                  bgcolor: "#fff",
                  px: 2.5,
                  py: 2,
                }}
              >
                {attachment && (
                  <Box
                    sx={{
                      mb: 1.3,
                      py: 0.8,
                      px: 1.3,

                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        "space-between",

                      borderRadius: 2,
                      bgcolor: "#f8fafc",
                      border:
                        "1px solid #edf1f5",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      minWidth={0}
                    >
                      <AttachFileRoundedIcon
                        sx={{
                          fontSize: 15,
                          color: "#94a3b8",
                        }}
                      />

                      <Typography
                        fontSize={10.5}
                        color="#64748b"
                        noWrap
                      >
                        {attachment.name}
                      </Typography>
                    </Stack>

                    <IconButton
                      size="small"
                      onClick={() =>
                        setAttachment(null)
                      }
                    >
                      <DeleteOutlineRoundedIcon
                        sx={{
                          fontSize: 16,
                        }}
                      />
                    </IconButton>
                  </Box>
                )}

                <Stack
                  direction="row"
                  alignItems="flex-end"
                  spacing={1}
                >
                  <Tooltip title="افزودن فایل">
                    <IconButton
                      component="label"
                      sx={{
                        width: 42,
                        height: 42,
                        flexShrink: 0,
                        borderRadius: 2,
                        bgcolor: "#f8fafc",
                        border:
                          "1px solid #e8edf3",
                        color: "#64748b",
                      }}
                    >
                      <AttachFileRoundedIcon
                        sx={{
                          fontSize: 18,
                        }}
                      />

                      <input
                        hidden
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,.pdf,.zip,.txt"
                        onChange={(e) =>
                          setAttachment(
                            e.target.files?.[0] ||
                            null
                          )
                        }
                      />
                    </IconButton>
                  </Tooltip>

                  <TextField
                    value={message}
                    onChange={(e) =>
                      setMessage(
                        e.target.value
                      )
                    }
                    placeholder="پاسخ خود را بنویسید..."
                    multiline
                    maxRows={4}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root":
                      {
                        bgcolor:
                          "#f8fafc",
                        borderRadius: 2,
                        fontSize: 12,
                        lineHeight: 1.9,

                        "& fieldset": {
                          borderColor:
                            "#e8edf3",
                        },
                      },
                    }}
                  />

                  <IconButton
                    type="submit"
                    disabled={
                      sending ||
                      !message.trim()
                    }
                    sx={{
                      width: 42,
                      height: 42,
                      flexShrink: 0,
                      borderRadius: 2,
                      bgcolor:
                        "primary.main",
                      color: "#fff",

                      "&:hover": {
                        bgcolor:
                          "primary.dark",
                      },

                      "&.Mui-disabled": {
                        bgcolor: "#e2e8f0",
                        color: "#94a3b8",
                      },
                    }}
                  >
                    {sending ? (
                      <CircularProgress
                        size={18}
                        color="inherit"
                      />
                    ) : (
                      <SendRoundedIcon
                        sx={{
                          fontSize: 18,
                        }}
                      />
                    )}
                  </IconButton>
                </Stack>

                <Box
                  mt={1}
                  display="flex"
                  justifyContent="flex-start"
                >
                  <Button
                    color="error"
                    size="small"
                    variant="text"
                    onClick={() =>
                      setConfirmClose(true)
                    }
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      fontSize: 10.5,
                      opacity: 0.75,
                    }}
                  >
                    بستن این تیکت
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Dialog>

      {/* Close confirmation */}
      <Dialog
        open={confirmClose}
        onClose={() =>
          !closing &&
          setConfirmClose(false)
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0.5,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 15,
            fontWeight: 700,
            pb: 1,
          }}
        >
          بستن تیکت
        </DialogTitle>

        <DialogContent>
          <Typography
            fontSize={12}
            color="#64748b"
            lineHeight={2}
          >
            بعد از بستن این تیکت، کاربر دیگر
            نمی‌تواند پیام جدیدی در این گفتگو
            ارسال کند.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            gap: 1,
          }}
        >
          <Button
            variant="text"
            color="inherit"
            disabled={closing}
            onClick={() =>
              setConfirmClose(false)
            }
            sx={{
              fontSize: 11,
            }}
          >
            انصراف
          </Button>

          <Button
            color="error"
            variant="contained"
            disabled={closing}
            onClick={handleCloseTicket}
            sx={{
              borderRadius: 2,
              boxShadow: "none",
              fontSize: 11,
            }}
          >
            {closing
              ? "در حال بستن..."
              : "بستن تیکت"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

